import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { useNavigate } from 'react-router-dom';
import { HandlingUnit, HandlingUnitStage, HandlingUnitLocation } from '../types/purchaseOrder';
import { getAllHandlingUnits } from '../api';

interface KanbanColumn {
  stage: HandlingUnitStage;
  title: string;
  icon: string;
  color: string;
  subtitle?: string;
}

const columns: KanbanColumn[] = [
  { 
    stage: HandlingUnitStage.Pending, 
    title: 'Pending', 
    icon: 'pi pi-clock',
    color: 'var(--yellow-500)'
  },
  { 
    stage: HandlingUnitStage.Received, 
    title: 'Received', 
    icon: 'pi pi-check-circle',
    color: 'var(--blue-500)'
  },
  { 
    stage: HandlingUnitStage.Painting, 
    title: 'Painting', 
    icon: 'pi pi-palette',
    color: 'var(--orange-500)'
  },
  { 
    stage: HandlingUnitStage.Completed, 
    title: 'Completed', 
    icon: 'pi pi-check',
    color: 'var(--green-500)',
    subtitle: 'Last 7 days'
  }
];

const HandlingUnitKanban: React.FC = () => {
  const [handlingUnits, setHandlingUnits] = useState<HandlingUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const toast = React.useRef<Toast>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadHandlingUnits();
  }, []);

  const loadHandlingUnits = async () => {
    try {
      const data = await getAllHandlingUnits();
      setHandlingUnits(data);
    } catch (error) {
      console.error('Error loading handling units:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load handling units',
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const getLocationTag = (location?: HandlingUnitLocation) => {
    if (!location) return <Tag value="No Location" severity="warning" />;
    
    const severityMap: Record<HandlingUnitLocation, "success" | "info" | "warning" | "danger"> = {
      [HandlingUnitLocation.A]: "success",
      [HandlingUnitLocation.B]: "info",
      [HandlingUnitLocation.C]: "warning",
      [HandlingUnitLocation.D]: "danger",
      [HandlingUnitLocation.E]: "warning"
    };

    return <Tag value={location} severity={severityMap[location]} />;
  };

  const handleCardClick = (huNumber: string) => {
    setExpandedCard(expandedCard === huNumber ? null : huNumber);
  };

  const handleViewDetails = (e: React.MouseEvent, unit: HandlingUnit) => {
    e.stopPropagation(); // Prevent card expansion when clicking the button
    if (unit.purchaseOrderItem?.purchaseOrderId) {
      navigate(`/purchase-orders/${unit.purchaseOrderItem.purchaseOrderId}`);
    } else {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Purchase order details not available',
        life: 3000
      });
    }
  };

  const formatDateTime = (dateTimeStr?: string) => {
    if (!dateTimeStr) return '';
    const date = new Date(dateTimeStr);
    return date.toLocaleString();
  };

  const renderHandlingUnitCard = (unit: HandlingUnit) => {
    const isExpanded = expandedCard === unit.huNumber;

    return (
      <Card
        key={unit.huNumber}
        className={`mb-2 p-2 cursor-pointer transition-all duration-200 ${isExpanded ? 'expanded-card' : ''}`}
        style={{ 
          minHeight: 'auto',
          transition: 'all 0.2s ease-in-out'
        }}
        onClick={() => handleCardClick(unit.huNumber)}
      >
        <div className={`flex flex-column gap-2 ${isExpanded ? '' : 'text-center'}`}>
          <div className={`flex ${isExpanded ? 'justify-content-between' : 'justify-content-center'} align-items-center`}>
            <span className="font-bold">{unit.huNumber}</span>
            {isExpanded ? getLocationTag(unit.location) : (
              <span className="ml-2 text-500">
                ({unit.receivedCount ?? '0'})
              </span>
            )}
          </div>
          
          {isExpanded && (
            <div className="text-sm expanded-content">
              <div>Location: {unit.location || 'Not Set'}</div>
              {unit.receivedCount !== undefined && (
                <div>Received Count: {unit.receivedCount}</div>
              )}
              {unit.handlingUnitOutboundTruckNumber && (
                <div>Truck: {unit.handlingUnitOutboundTruckNumber}</div>
              )}
              {unit.huTruckDateTime && (
                <div>Received: {formatDateTime(unit.huTruckDateTime)}</div>
              )}
              <div className="mt-2">
                <Button
                  label="View PO Details"
                  icon="pi pi-external-link"
                  className="p-button-sm"
                  onClick={(e) => handleViewDetails(e, unit)}
                />
              </div>
            </div>
          )}
        </div>
      </Card>
    );
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="card">
      <Toast ref={toast} />
      
      <div className="flex justify-content-between mb-3">
        <h1>Handling Unit Board</h1>
        <Button
          icon="pi pi-refresh"
          onClick={loadHandlingUnits}
          tooltip="Refresh"
        />
      </div>

      <div className="grid">
        {columns.map(column => (
          <div 
            key={column.stage}
            className="col-12 md:col-3"
          >
            <div 
              className="surface-card p-3 border-round mb-3"
              style={{ borderTop: `5px solid ${column.color}` }}
            >
              <div className="flex align-items-center mb-3">
                <i className={`${column.icon} mr-2`} style={{ color: column.color }}></i>
                <div className="flex flex-column">
                  <span className="font-bold">{column.title}</span>
                  {column.subtitle && (
                    <small className="text-500">{column.subtitle}</small>
                  )}
                </div>
                <span className="ml-2 text-500">
                  ({handlingUnits.filter(u => u.stage === column.stage).length})
                </span>
              </div>
              
              <div className="kanban-column">
                {handlingUnits
                  .filter(unit => unit.stage === column.stage)
                  .map(renderHandlingUnitCard)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HandlingUnitKanban;
